(() => {
  const ADD_SELECTOR = '[data-upsell-add]';
  const SLIDER_SELECTOR = '[data-cart-upsell-slider]';
  const initializedTracks = new WeakSet();

  /* Upsell discount percentages, keyed by product handle. Rendered into the
     page by cart-drawer.liquid from snippets/upsell-discount-pct.liquid, which
     mirrors the automatic discounts configured in Shopify admin. Parsed once.

     A handle that is absent returns 0, meaning "no automatic discount" — show
     the plain price rather than promising a saving checkout will not apply. */
  let upsellDiscountMap = null;

  function upsellDiscountPct(handle) {
    if (upsellDiscountMap === null) {
      upsellDiscountMap = {};
      try {
        const el = document.getElementById('moksae-upsell-discount-map');
        if (el && el.textContent.trim()) {
          upsellDiscountMap = JSON.parse(el.textContent);
        }
      } catch (e) {
        /* Malformed or missing map: fall back to charging no discount rather
           than guessing a rate and misquoting the price. */
        upsellDiscountMap = {};
      }
    }
    const pct = upsellDiscountMap[handle];
    return typeof pct === 'number' && pct > 0 ? pct : 0;
  }

  function getSliderElements(slider) {
    const track = slider.querySelector('[data-upsell-track]');
    const prevButton = slider.querySelector('[data-upsell-prev]');
    const nextButton = slider.querySelector('[data-upsell-next]');
    const cards = slider.querySelectorAll('[data-upsell-card]');

    return { track, prevButton, nextButton, cards };
  }

  function updateArrowState(slider) {
    const { track, prevButton, nextButton, cards } = getSliderElements(slider);
    if (!track || !prevButton || !nextButton) return;

    const hasOverflow = track.scrollWidth > track.clientWidth + 2;
    const atStart = track.scrollLeft <= 4;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;

    if (!hasOverflow || cards.length <= 1) {
      prevButton.classList.add('is-hidden');
      nextButton.classList.add('is-hidden');
      prevButton.disabled = true;
      nextButton.disabled = true;
      return;
    }

    prevButton.disabled = atStart;
    nextButton.disabled = atEnd;
    prevButton.classList.toggle('is-hidden', atStart);
    nextButton.classList.toggle('is-hidden', atEnd);
  }

  function scrollSlider(slider, direction) {
    const { track } = getSliderElements(slider);
    if (!track) return;

    const firstCard = track.querySelector('[data-upsell-card]');
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : track.clientWidth * 0.85;
    const computed = window.getComputedStyle(track);
    const gap = parseFloat(computed.columnGap || computed.gap || '0');
    const amount = (cardWidth + gap) * direction;

    track.scrollBy({ left: amount, behavior: 'smooth' });
  }

  function initializeSlider(slider) {
    const { track } = getSliderElements(slider);
    if (!track || initializedTracks.has(track)) return;

    initializedTracks.add(track);
    track.addEventListener('scroll', () => updateArrowState(slider), { passive: true });
    updateArrowState(slider);
  }

  async function loadDynamicRecommendations() {
    const container = document.getElementById('cart-recommendations');
    if (!container || container.classList.contains('is-loading') || container.dataset.customRendered === 'true' || container.dataset.loadedProductId === container.dataset.productId) {
      return;
    }

    const productId = container.dataset.productId;
    if (!productId) return;

    container.classList.add('is-loading');
    container.dataset.loadedProductId = productId;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=2`);
      if (!response.ok) throw new Error('API failed');

      const data = await response.json();
      const products = data.products || [];

      const track = document.getElementById('cart-recommendations-track');
      if (!track) return;

      track.innerHTML = '';

      if (products.length === 0) {
        container.classList.add('hidden');
        return;
      }

      // Check which variants are already in the cart
      const cartDrawer = document.querySelector('cart-drawer');
      const cartVariantIds = Array.from(cartDrawer?.querySelectorAll('[data-quantity-variant-id]') || [])
        .map(el => Number(el.dataset.quantityVariantId));

      const escapeHtml = (str) =>
        String(str ?? '').replace(/[&<>"']/g, (ch) => (
          { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
        ));

      products.forEach((product) => {
        if (!product.featured_image) return;

        const isAvailable = product.available !== false;
        if (!isAvailable) return;

        const variant = product.variants.find((v) => v.available);
        if (!variant) return;

        const isAdded = cartVariantIds.includes(variant.id);
        const title = escapeHtml(product.title);
        const priceNum = variant.price / 100;
        let discountedPriceNum = priceNum;
        let strikePriceNum = null;
        /* Resolved from the real Shopify discounts via the map rendered in
           cart-drawer.liquid. This used to be hardcoded to 20% for every
           product, so 15% items advertised a saving checkout never applied. */
        let discountPct = upsellDiscountPct(product.handle);

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          strikePriceNum = variant.compare_at_price / 100;
          discountedPriceNum = priceNum;
          discountPct = Math.round(((strikePriceNum - discountedPriceNum) / strikePriceNum) * 100);
        } else if (discountPct > 0) {
          strikePriceNum = priceNum;
          discountedPriceNum = Math.round(priceNum * (100 - discountPct)) / 100;
        } else {
          /* No automatic discount: show the plain price, no invented saving. */
          strikePriceNum = null;
          discountedPriceNum = priceNum;
        }

        const priceFormatted = discountedPriceNum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const strikeFormatted = strikePriceNum ? strikePriceNum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;

        const buttonHtml = `<button type="button" class="cart-recommendations__add" data-upsell-add data-variant-id="${variant.id}" ${isAdded ? 'disabled' : ''}>${isAdded ? 'Added' : 'Add'}</button>`;

        const cardHtml = `
          <article class="cart-recommendations__card" data-upsell-card style="width: 100%; position: relative;">
            ${discountPct > 0 ? `<span class="cart-recommendations__badge" style="position: absolute; top: 8px; right: 8px; background: #e5ffe8; color: #1e5a26; font-size: 0.75rem; font-weight: 700; padding: 2px 6px; border-radius: 6px; z-index: 2; border: 1px solid rgba(30, 90, 38, 0.2);">Add &amp; save ${discountPct}%</span>` : ''}
            <a href="${product.url}" class="cart-recommendations__image-link" aria-label="${title}">
              <img
                src="${product.featured_image}"
                class="cart-recommendations__image"
                alt="${title}"
                loading="lazy"
                width="220"
                height="220"
              />
            </a>
            <p class="cart-recommendations__product-title">${title}</p>
            <div class="cart-recommendations__footer" style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
              <div class="cart-recommendations__prices" style="display: flex; flex-direction: column;">
                <span class="cart-recommendations__price" style="color: #573862; font-weight: 600; font-size: 0.95rem;">Rs. ${priceFormatted}</span>
                ${strikeFormatted ? `<s style="color: #999; font-size: 0.8rem; font-weight: normal;">Rs. ${strikeFormatted}</s>` : ''}
              </div>
              ${buttonHtml}
            </div>
          </article>
        `;
        track.insertAdjacentHTML('beforeend', cardHtml);
      });

      container.classList.remove('hidden');
    } catch (err) {
      console.error('[CartRecommendations] load failed:', err);
      container.dataset.loadedProductId = ''; // reset on failure
    } finally {
      container.classList.remove('is-loading');
    }
  }

  function initializeAllSliders() {
    document.querySelectorAll(SLIDER_SELECTOR).forEach((slider) => initializeSlider(slider));
    loadDynamicRecommendations();
  }

  async function addUpsellToCart(button) {
    if (!button || button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    button.disabled = true;
    const originalLabel = button.textContent.trim();
    button.textContent = 'Adding...';

    // All cart mutate+render work runs on the global queue (defined in
    // cart.js) so it can't interleave with a quantity-change or reward cycle.
    const enqueue = window.cartOpsQueue ? (t) => window.cartOpsQueue.enqueue(t) : (t) => t();

    try {
      await enqueue(async () => {
        // Always add one unit via /cart/add.js. The server merges it into the
        // variant's existing line (and the BxGy discount engine re-splits as
        // needed), and inventory limits come back as a clean 422. The previous
        // code incremented an existing line via /cart/change.js with a positional
        // `line` index captured at render time — any cart mutation since render
        // (free-gift add/remove, BxGy re-split, another add) shifts the indexes,
        // so the change silently hit the WRONG line and corrupted the cart.
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });

        const state = await response.json();

        if (!response.ok || state.status) {
          // Surface the real reason (e.g. "You can only add N of this item")
          // instead of failing silently.
          throw new Error(state.description || state.message || 'Unable to update cart item');
        }
        const cartDrawer = document.querySelector('cart-drawer');

        if (cartDrawer && state.sections) {
          // Empty -> non-empty transition: ensure drawer state class matches cart contents
          if (typeof state.item_count === 'number') {
            cartDrawer.classList.toggle('is-empty', state.item_count === 0);
          } else {
            cartDrawer.classList.remove('is-empty');
          }

          cartDrawer.renderContents({
            id: variantId,
            sections: state.sections,
          });
        }
      });

      button.textContent = 'Added';

      // Keep the reward milestones in sync: this add can cross a free-gift
      // threshold, and the reward system otherwise only runs on Dawn's
      // cartUpdate event, which this code path doesn't publish. Queued as its
      // own task AFTER the add task above (never nested inside it — deadlock).
      if (typeof window.scheduleRewardValidation === 'function') {
        window.scheduleRewardValidation();
      }
    } catch (error) {
      console.error('[CartUpsell] add failed:', error);
      button.disabled = false;
      button.textContent = originalLabel;
      // Show the reason in the drawer's error region instead of failing silently.
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) {
        errors.textContent = error.message || (window.cartStrings && window.cartStrings.error) || 'Unable to add to cart';
        setTimeout(() => {
          if (errors.textContent) errors.textContent = '';
        }, 5000);
      }
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest(ADD_SELECTOR);
    if (!button) return;

    event.preventDefault();
    addUpsellToCart(button);
  });

  document.addEventListener('click', (event) => {
    const prev = event.target.closest('[data-upsell-prev]');
    const next = event.target.closest('[data-upsell-next]');
    if (!prev && !next) return;

    const slider = event.target.closest(SLIDER_SELECTOR);
    if (!slider) return;

    event.preventDefault();
    scrollSlider(slider, next ? 1 : -1);

    // Update state after smooth scroll settles
    window.setTimeout(() => updateArrowState(slider), 260);
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll(SLIDER_SELECTOR).forEach((slider) => updateArrowState(slider));
  });

  // Re-init after the drawer's HTML is replaced on cart changes. Scoped to the
  // drawer (not body) and coalesced to one run per frame so unrelated page
  // mutations don't trigger repeated slider scans.
  let initScheduled = false;
  const scheduleInit = () => {
    if (initScheduled) return;
    initScheduled = true;
    window.requestAnimationFrame(() => {
      initScheduled = false;
      initializeAllSliders();
    });
  };
  const observerTarget = document.querySelector('cart-drawer') || document.body;
  const observer = new MutationObserver(scheduleInit);
  observer.observe(observerTarget, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllSliders, { once: true });
  } else {
    initializeAllSliders();
  }
})();
